import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import EditorialLoader from "../components/loader/EditorialLoader";

/* ======================
   TYPES
====================== */
export type LoaderConfig = {
  title?: string;
  subtitle?: string;
  label?: string;
  duration?: number;
  force?: boolean; // 👈 cho phép ép show loader
};

type LoaderCtx = {
  show: (cfg?: LoaderConfig) => void;
  hide: () => void;
  isLoading: boolean;
};

/* ======================
   CONTEXT
====================== */
const LoaderContext = createContext<LoaderCtx | null>(null);

/* ======================
   PROVIDER
====================== */
export function LoaderProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState<LoaderConfig>({});

  const timerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  /* ======================
     ACCESSIBILITY
  ====================== */
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ======================
     CLEAR TIMER
  ====================== */
  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  /* ======================
     SHOW
  ====================== */
  const show = useCallback(
    (cfg?: LoaderConfig) => {
      if (prefersReducedMotion) return;

      // đang loading + không force → bỏ
      if (open && !cfg?.force) return;

      clearTimer();
      setConfig(cfg || {});
      setOpen(true);
    },
    [open, prefersReducedMotion]
  );

  /* ======================
     HIDE
  ====================== */
  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, []);

  /* ======================
     AUTO HIDE
  ====================== */
  useEffect(() => {
    if (!open) return;

    const duration = config.duration ?? 500;

    timerRef.current = window.setTimeout(() => {
      if (!mountedRef.current) return;
      setOpen(false);
    }, duration);

    return clearTimer;
  }, [open, config.duration]);

  /* ======================
     CLEANUP
  ====================== */
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, []);

  /* ======================
     RENDER
  ====================== */
  return (
    <LoaderContext.Provider
      value={{
        show,
        hide,
        isLoading: open,
      }}
    >
      {children}

      {/* ===== GLOBAL EDITORIAL LOADER ===== */}
      {open && <EditorialLoader {...config} />}
    </LoaderContext.Provider>
  );
}

/* ======================
   HOOK
====================== */
export function useGlobalLoader(): LoaderCtx {
  const ctx = useContext(LoaderContext);
  if (!ctx) {
    throw new Error(
      "useGlobalLoader must be used inside LoaderProvider"
    );
  }
  return ctx;
}
